/**
 * Payment — the third step of a tiffin order.
 *
 * The same rails as a donation, chosen the same way; a tiffin order is paid
 * through exactly the payment code a donation is. Nothing is charged here:
 * Review is where the order is placed and the payment started.
 */

import { useState } from 'react';
import { StyleSheet } from 'react-native';

import { formatMoney, normaliseWalletMsisdn } from '@mfa/domain';
import { space } from '../../theme';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { CheckoutHeader } from '../../components/donation/CheckoutHeader';
import { TIFFIN_STEPS } from '../../components/donation/CheckoutStepper';
import { LockIcon } from '../../components/common/Icon';
import {
  PaymentAssurance,
  PaymentMethodPicker,
  paymentReady,
} from '../../components/checkout/PaymentMethodPicker';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { Text } from '../../components/common/Text';
import { useTiffinStore } from './tiffinStore';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';

export function TiffinPaymentScreen({ navigation }: DonorScreenProps<'TiffinPayment'>) {
  const t = useT();
  const quote = useTiffinStore((s) => s.quote);
  const selected = useTiffinStore((s) => s.paymentMethodCode);
  const setPaymentMethod = useTiffinStore((s) => s.setPaymentMethod);
  const mobile = useTiffinStore((s) => s.customer.mobileNumber);
  const walletMsisdn = useTiffinStore((s) => s.walletMsisdn);
  const setWalletMsisdn = useTiffinStore((s) => s.setWalletMsisdn);
  const [walletTouched, setWalletTouched] = useState(false);

  // The customer's own number to start with; editable, since a household
  // often pays from one person's wallet.
  const walletValue = walletMsisdn ?? mobile ?? '';
  const canContinue = Boolean(quote) && paymentReady(selected, walletValue);

  return (
    <ScreenShell
      leading="back"
      onBackPress={navigation.goBack}
      testID="screen-tiffin-payment"
      subHeader={
        <CheckoutHeader
          current={4}
          steps={TIFFIN_STEPS}
          title={t('Make Payment')}
          subtitle={t('Choose how you would like to pay')}
        />
      }
      pinnedFooter={
        <Button
          label={quote ? `${t('Pay')} ${formatMoney(quote.totalAmount)}` : t('Continue')}
          onPress={() => {
            setWalletTouched(true);
            if (!canContinue) return;
            if (selected === 'easypaisa') setWalletMsisdn(normaliseWalletMsisdn(walletValue));
            navigation.navigate('TiffinReview');
          }}
          disabled={!canContinue}
          leadingIcon={<LockIcon />}
          testID="cta-tiffin-pay"
        />
      }
    >
      {quote ? (
        <Card style={styles.total} testID="tiffin-payment-total">
          <Text variant="label" tone="secondary">{t('Total Amount')}</Text>
          <Text variant="amount" tone="brand">
            {formatMoney(quote.totalAmount)}
          </Text>
        </Card>
      ) : null}

      <PaymentMethodPicker
        selected={selected}
        onSelect={setPaymentMethod}
        walletValue={walletValue}
        onWalletChange={(value) => setWalletMsisdn(value)}
        walletTouched={walletTouched}
        onWalletBlur={() => setWalletTouched(true)}
        onContact={() => navigation.navigate('Contact')}
        emptyMessage="Online payment is not set up yet. Please try again later, or contact us to order."
      />

      <PaymentAssurance />
    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  total: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: space.lg,
  },
});
